// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { GameDefinition, RoundSummary, TourGameScore, TourRoundGameScore } from './types';
import { getGameDisplayName } from '@/features/game-definitions/i18n';

export const PLAYER_COL_WIDTH = 100;
export const GAME_COL_WIDTH = 48;
export const TOTAL_COL_WIDTH = 40;

export const buildGameNameLookup = (gameDefs: GameDefinition[], fallback: string) =>
  new Map(gameDefs.map((game) => [game.id, getGameDisplayName(game, fallback)]));

export const buildPlayerRows = (
  scoreboard: TourGameScore[],
  nameLookup: Map<string, string>,
  fallback: string
) =>
  Array.from(new Set(scoreboard.map((entry) => entry.playerId)))
    .map((playerId) => {
      const perGame = new Map<string, number>();
      let total = 0;
      scoreboard
        .filter((entry) => entry.playerId === playerId)
        .forEach((entry) => {
          perGame.set(entry.gameId, entry.scorePoints);
          total += entry.scorePoints;
        });
      return {
        playerId,
        name: nameLookup.get(playerId) ?? fallback,
        perGame,
        total,
      };
    })
    .sort((a, b) => b.total - a.total);

export const buildOrderedRounds = (rounds: RoundSummary[]) =>
  rounds.slice().sort((a, b) => {
    const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
    return aTime - bTime;
  });

export const buildRoundScoreMap = (roundScoreboard: TourRoundGameScore[]) => {
  const roundGameMap = new Map<string, number>();
  roundScoreboard.forEach((score) => {
    if (!score.roundId || !score.gameId || !score.playerId) return;
    const key = `${score.playerId}:${score.gameId}:${score.roundId}`;
    roundGameMap.set(key, score.scorePoints);
  });
  return roundGameMap;
};

export const buildPerGamePerRoundRows = (
  playerIds: string[],
  orderedGameIds: string[],
  gameRoundIds: string[],
  roundGameMap: Map<string, number>,
  nameLookup: Map<string, string>,
  fallback: string
) =>
  playerIds
    .map((playerId) => {
      let total = 0;
      const pointsByGameRound = new Map<string, number>();
      orderedGameIds.forEach((gameId) => {
        gameRoundIds.forEach((roundId) => {
          const key = `${playerId}:${gameId}:${roundId}`;
          const points = roundGameMap.get(key) ?? 0;
          pointsByGameRound.set(key, points);
          total += points;
        });
      });
      return {
        playerId,
        name: nameLookup.get(playerId) ?? fallback,
        pointsByGameRound,
        total,
      };
    })
    .sort((a, b) => b.total - a.total);
